import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { LangProvider } from "@/i18n";
import { AuthProvider, useAuth } from "@/auth";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import Home from "@/pages/Home";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";

function AuthCallback() {
  const { completeAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    api.post("/auth/google-session", { session_id: sessionId })
      .then((r) => {
        completeAuth(r.data);
        navigate(location.pathname || "/", { replace: true, state: { user: r.data } });
      })
      .catch(() => navigate("/", { replace: true }));
  }, []);

  return <div data-testid="auth-callback" className="min-h-screen bg-[#FAF9F6]" />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const scrollToHash = () => {
      const el = document.querySelector(window.location.hash);
      if (el) el.scrollIntoView();
    };
    const wantsHash = window.location.hash && !window.location.hash.includes("session_id=");

    // Su dispositivi touch (iPhone/Android) niente Lenis: lo scroll nativo iOS
    // tiene le barre fisse incollate, Lenis le disallinea durante lo scorrimento.
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    if (isTouch) {
      if (wantsHash) {
        const timers = [setTimeout(scrollToHash, 700), setTimeout(scrollToHash, 2200)];
        return () => timers.forEach(clearTimeout);
      }
      return;
    }

    const lenis = new Lenis({ duration: 1.25, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    if (wantsHash) {
      const go = (immediate) => {
        const el = document.querySelector(window.location.hash);
        if (el) lenis.scrollTo(el, { duration: 1.0, offset: -64, immediate });
      };
      const timers = [
        setTimeout(() => go(false), 700),
        setTimeout(() => go(true), 2200),
      ];
      return () => {
        timers.forEach(clearTimeout);
        cancelAnimationFrame(raf);
        lenis.destroy();
      };
    }

    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-center" />
      </AuthProvider>
    </LangProvider>
  );
}

export default App;
